import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { FlowContext, LogEntry, OrderSnapshot, RunnerStatus, StepRun, StepStatus } from "../flow/types";
import { stepById } from "../flow/flowGraph";

export const TESTER_HISTORY_KEY = "ORCA_ORDER_FLOW_TESTER_HISTORY";
export const HISTORY_LIMIT = 10;
const MAX_LOG_ENTRIES = 150;

export type HistoryRunStatus = "completed" | "failed" | "cancelled";

export interface ExecutionHistoryStep {
  id: string;
  name: string;
  status: StepStatus;
  note?: string;
  error?: string;
}

export interface ExecutionHistoryLogLine {
  at: number;
  offsetMs: number;
  level: LogEntry["level"];
  stepId?: string;
  message: string;
}

export interface ExecutionHistoryEntry {
  id: string;
  startedAt: number;
  endedAt?: number;
  durationMs?: number;
  status: HistoryRunStatus;
  targetStepId?: string;
  targetLabel?: string;
  targetReached: boolean;
  enquiryOrderNo?: string;
  enquiryId?: string;
  childOrderNo?: string;
  childOrderId?: string;
  orderStatus?: string;
  customerName?: string;
  supplierName?: string;
  supplierFlow?: "lean" | "autonomous";
  completedSteps: number;
  skippedSteps: number;
  failedStepId?: string;
  failedStepName?: string;
  lastError?: string;
  totalApiCalls: number;
  totalPollChecks: number;
  eventsProcessed: number;
  steps: ExecutionHistoryStep[];
  log: ExecutionHistoryLogLine[];
}

interface ExecutionHistoryState {
  entries: ExecutionHistoryEntry[];
  upsert: (entry: ExecutionHistoryEntry) => void;
  remove: (id: string) => void;
  clear: () => void;
}

/** Runner slice needed to persist a run — kept here to avoid importing useFlowRunner. */
export interface HistorySource {
  status: RunnerStatus;
  targetStepId?: string;
  plannedStepIds: string[];
  branchStepIds: string[];
  runs: Record<string, StepRun>;
  failedStepId?: string;
  log: LogEntry[];
  snapshot?: OrderSnapshot;
  ctx?: FlowContext;
  startedAt?: number;
  endedAt?: number;
  totalPollChecks: number;
  totalApiCalls: number;
  eventsProcessed: number;
  lastError?: string;
  targetReached: boolean;
}

export const formatOrderRefs = (refs: {
  enquiryNo?: string;
  enquiryId?: string;
  orderNo?: string;
  orderId?: string;
}): string => {
  const parts: string[] = [];
  if (refs.enquiryNo || refs.enquiryId) {
    parts.push(`enquiryNo=${refs.enquiryNo || "—"} _id=${refs.enquiryId || "—"}`);
  }
  if (refs.orderNo || refs.orderId) {
    parts.push(`orderNo=${refs.orderNo || "—"} _id=${refs.orderId || "—"}`);
  }
  return parts.join("  ·  ");
};

const isTerminal = (status: RunnerStatus): status is HistoryRunStatus =>
  status === "completed" || status === "failed" || status === "cancelled";

/** Compact, token-free snapshot of a finished run for localStorage. */
export const toHistoryEntry = (state: HistorySource): ExecutionHistoryEntry | null => {
  if (!state.startedAt || !isTerminal(state.status)) return null;

  const stepIds = [...state.plannedStepIds, ...state.branchStepIds.filter((id) => !state.plannedStepIds.includes(id))];
  const runs = stepIds.map((id) => state.runs[id]).filter(Boolean);
  const log = state.log.slice(-MAX_LOG_ENTRIES).map((e) => ({
    at: e.at,
    offsetMs: e.offsetMs,
    level: e.level,
    stepId: e.stepId,
    message: e.message,
  }));

  return {
    id: `exec-${state.startedAt}`,
    startedAt: state.startedAt,
    endedAt: state.endedAt,
    durationMs: state.endedAt ? state.endedAt - state.startedAt : undefined,
    status: state.status,
    targetStepId: state.targetStepId,
    targetLabel: state.targetStepId ? stepById(state.targetStepId)?.targetLabel : undefined,
    targetReached: Boolean(state.targetReached),
    enquiryOrderNo: state.snapshot?.enquiry?.orderNo,
    enquiryId: state.snapshot?.enquiry?.orderId,
    childOrderNo: state.snapshot?.child?.orderNo,
    childOrderId: state.snapshot?.child?.orderId,
    orderStatus: state.snapshot?.child?.orderStatus || state.snapshot?.enquiry?.orderStatus,
    customerName: state.ctx?.config.customerName,
    supplierName: state.ctx?.supplier.name,
    supplierFlow: state.ctx?.supplier.isLeanFlow ? "lean" : state.ctx?.supplier ? "autonomous" : undefined,
    completedSteps: runs.filter((r) => r.status === "completed").length,
    skippedSteps: runs.filter((r) => r.status === "skipped").length,
    failedStepId: state.failedStepId,
    failedStepName: state.failedStepId ? stepById(state.failedStepId)?.name : undefined,
    lastError: state.lastError || (state.failedStepId ? state.runs[state.failedStepId]?.error?.reason : undefined),
    totalApiCalls: state.totalApiCalls,
    totalPollChecks: state.totalPollChecks,
    eventsProcessed: state.eventsProcessed,
    steps: stepIds.map((id) => {
      const run = state.runs[id];
      return {
        id,
        name: stepById(id)?.name || id,
        status: run?.status || "pending",
        note: run?.note,
        error: run?.error?.reason,
      };
    }),
    log,
  };
};

export const useExecutionHistory = create<ExecutionHistoryState>()(
  persist(
    (set) => ({
      entries: [],
      upsert: (entry) =>
        set((state) => {
          const rest = state.entries.filter((e) => e.id !== entry.id);
          return { entries: [entry, ...rest].slice(0, HISTORY_LIMIT) };
        }),
      remove: (id) => set((state) => ({ entries: state.entries.filter((e) => e.id !== id) })),
      clear: () => set({ entries: [] }),
    }),
    {
      name: TESTER_HISTORY_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries }),
    }
  )
);
